import { ActionTypes } from '../Constants';
import Storage from '../Storage';
import HostInfo from '../model/HostInfo';
import User from '../model/User';
import KioskVersionSet from '../model/KioskVersionSet';
import LogURL from '../model/LogURL';
import AppInfo from '../model/AppInfo';
import SosdbRoleAction from '../model/SosdbRoleAction';
import SosdbG2ConvertHistory from '../model/SosdbG2ConvertHistory';

export function kvsListReducer(kvsList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_KVS: {
      const kvsExists = kvsList.find((kvs) => {
        return kvs.name === action.kvs.name;
      });

      if (!kvsExists) {
        // new kvs added to end of list
        return [...kvsList, action.kvs];
      }

      // return new list with existing kvs merged with new kvs
      return kvsList.map((kvs) => {
        // merge kvs object if exists
        if (kvs.name === action.kvs.name) {
          return new KioskVersionSet({
            ...kvs,
            ...action.kvs,
          });
        } else {
          return kvs;
        }
      });
    }
    case ActionTypes.DELETE_KVS:
      return kvsList.filter((kvs) => kvs.name !== action.name);

    case ActionTypes.ADD_KVS_LIST:
      return action.kvsList;

    default:
      return kvsList;
  }
}

export function kvsHistoryListReducer(kvsHistoryList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_KVS_HISTORY_LIST:
      return action.kvsHistoryList;

    default:
      return kvsHistoryList;
  }
}

export function appInfoListReducer(appInfoList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_APP_INFO: {
      const appInfoExists = appInfoList.find((appInfo) => {
        return appInfo.id === action.appInfo.id;
      });

      if (!appInfoExists) {
        // new appInfo added to end of list
        return [...appInfoList, action.appInfo];
      }

      // return new list with existing appInfo merged with new appInfo
      return appInfoList.map((appInfo) => {
        // merge appInfo object if exists
        if (appInfo.id === action.appInfo.id) {
          return new AppInfo({
            ...appInfo,
            ...action.appInfo,
          });
        } else {
          return appInfo;
        }
      });
    }
    case ActionTypes.DELETE_APP_INFO:
      return appInfoList.filter((appInfo) => {
        return appInfo.id !== action.appInfo.id;
      });

    case ActionTypes.ADD_APP_INFO_LIST:
      return action.appInfoList;

    default:
      return appInfoList;
  }
}

export function roleActionListReducer(roleActionList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_ROLE_ACTION: {
      const roleActionExists = roleActionList.find((roleAction) => {
        return roleAction.id === action.roleAction.id;
      });

      if (!roleActionExists) {
        // new roleAction added to end of list
        return [...roleActionList, action.roleAction];
      }

      // return new list with existing roleAction merged with new roleAction
      return roleActionList.map((roleAction) => {
        // merge roleAction object if exists
        if (roleAction.id === action.roleAction.id) {
          return new SosdbRoleAction({
            ...roleAction,
            ...action.roleAction,
          });
        } else {
          return roleAction;
        }
      });
    }
    case ActionTypes.DELETE_ROLE_ACTION:
      return roleActionList.filter((roleAction) => roleAction.id !== action.id);

    case ActionTypes.ADD_ROLE_ACTION_LIST:
      return action.roleActionList;

    default:
      return roleActionList;
  }
}

export function userListReducer(userList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_USER: {
      const userExists = userList.find((user) => {
        return user.email === action.user.email;
      });

      if (!userExists) {
        // new user added to end of list
        return [...userList, action.user];
      }

      // return new list with existing user merged with new user
      return userList.map((user) => {
        // merge user object if exists
        if (user.email === action.user.email) {
          return new User({
            ...user,
            ...action.user,
          });
        } else {
          return user;
        }
      });
    }
    case ActionTypes.DELETE_USER:
      return userList.filter((user) => user.email !== action.email);

    case ActionTypes.ADD_USER_LIST:
      return action.userList;

    default:
      return userList;
  }
}

export function loginAttemptListReducer(loginAttemptList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_LOGIN_ATTEMPT_LIST:
      return action.loginAttemptList;

    default:
      return loginAttemptList;
  }
}

export function hostInfoListReducer(hostInfoList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_HOST_INFO: {
      // if emtpy hostInfo object
      if (!action.hostInfo || !action.hostInfo.id) {
        return hostInfoList;
      }
      const hostInfoExists = hostInfoList.find((h) => {
        return h.id === action.hostInfo.id;
      });

      action.hostInfo.isDirty = true;

      if (!hostInfoExists) {
        // new hostInfo added to end of list
        return [...hostInfoList, action.hostInfo];
      }

      // return new list with existing hostInfo merged with new hostInfo
      return hostInfoList.map((hostInfo) => {
        if (hostInfo.id === action.hostInfo.id) {
          return new HostInfo({
            ...hostInfo,
            ...action.hostInfo,
          });
        } else {
          return hostInfo;
        }
      });
    }
    case ActionTypes.DELETE_HOST_INFO: {
      return hostInfoList.filter((h) => {
        return h.id !== action.id;
      });
    }

    case ActionTypes.ADD_HOST_INFO_LIST: {
      let dirtyLookup = new Map();
      let dirtyList = hostInfoList.filter((h) => h.isDirty);
      dirtyList.forEach((h) => {
        dirtyLookup.set(h.id, h);
      });
      if (dirtyList.length > 0) {
        // return new list after merging existing list entries with updated/dirty entries
        return action.hostInfoList.map((h) => {
          if (dirtyLookup.has(h.id)) {
            return new HostInfo({
              ...h,
              ...dirtyLookup.get(h.id),
            });
          }
          return h;
        });
      }
      return action.hostInfoList;
    }

    default:
      return hostInfoList;
  }
}

export function hostInfoListStatusReducer(
  hostInfoListStatus = {
    isLoading: null,
    success: false,
    errorMessage: '',
  },
  action
) {
  switch (action.type) {
    case ActionTypes.START_GETTING_HOST_INFO_LIST:
      return {
        isLoading: true,
        success: false,
        errorMessage: '',
      };
    case ActionTypes.FINISH_GETTING_HOST_INFO_LIST:
      return {
        isLoading: false,
        success: true,
        errorMessage: '',
      };
    case ActionTypes.ERROR_GETTING_HOST_INFO_LIST:
      return {
        isLoading: false,
        success: false,
        errorMessage: action.errorMessage,
      };
    default:
      return hostInfoListStatus;
  }
}

export function authReducer(
  auth = {
    isAuthenticated: false,
    user: new User(),
    errorMessage: '',
  },
  action
) {
  // console.log('authReducer', action, auth);
  switch (action.type) {
    case ActionTypes.UPDATE_PROFILE:
      return {
        isAuthenticated: auth.isAuthenticated,
        user: action.user,
        errorMessage: '',
      };
    case ActionTypes.LOGIN_SUCCESS:
      return {
        isAuthenticated: true,
        user: action.user,
        errorMessage: '',
      };
    case ActionTypes.LOGIN_FAILURE:
      return {
        isAuthenticated: false,
        user: auth.user,
        errorMessage: action.errorMessage,
      };
    case ActionTypes.LOGOUT_SUCCESS:
      return {
        isAuthenticated: false,
        user: new User(),
        errorMessage: '',
      };
    default:
      return auth;
  }
}

export function logURLListReducer(logURLList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_LOG_URL: {
      const logURLExists = logURLList.find((logURL) => {
        return logURL.id === action.logURL.id;
      });

      if (!logURLExists) {
        // new logURL added to end of list
        return [...logURLList, action.logURL];
      }

      // return new list with existing logURL merged with new logURL
      return logURLList.map((logURL) => {
        // merge logURL object if exists
        if (logURL.id === action.logURL.id) {
          return new LogURL({
            ...logURL,
            ...action.logURL,
          });
        } else {
          return logURL;
        }
      });
    }
    case ActionTypes.DELETE_LOG_URL:
      return logURLList.filter((logURL) => logURL.id !== action.id);

    case ActionTypes.ADD_LOG_URL_LIST:
      return action.logURLList;

    default:
      return logURLList;
  }
}

export function selectedHostInfoIdListReducer(selectedHostInfoIdList = [], action) {
  switch (action.type) {
    case ActionTypes.SELECT_HOSTINFO_ID: {
      const exists = selectedHostInfoIdList.find((hostInfoId) => {
        return hostInfoId === action.hostInfoId;
      });

      if (!exists) {
        return [...selectedHostInfoIdList, action.hostInfoId];
      }
      return selectedHostInfoIdList;
    }

    case ActionTypes.SELECT_HOSTINFO_ID_LIST: {
      const hostInfoIdList = [...selectedHostInfoIdList, ...action.hostInfoIdList];
      const hostInfoIdSet = new Set(hostInfoIdList);
      return [...hostInfoIdSet];
    }

    case ActionTypes.UNSELECT_HOSTINFO_ID:
      return selectedHostInfoIdList.filter((hostInfoId) => hostInfoId !== action.hostInfoId);

    case ActionTypes.UNSELECT_ALL_HOSTINFO_ID_LIST:
      return [];

    default:
      return selectedHostInfoIdList;
  }
}

export function selectedKVSReducer(kvs = new KioskVersionSet(), action) {
  switch (action.type) {
    case ActionTypes.SET_KVS:
      return action.kvs;

    default:
      return kvs;
  }
}

export function xpressTemplateListReducer(xpressTemplateList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_XPRESS_TEMPLATE_LIST:
      return action.payload;

    default:
      return xpressTemplateList;
  }
}

export function g2ConvertHistoryListReducer(g2ConvertHistoryList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_G2_CONVERT_HISTORY_LIST:
      return action.g2ConvertHistoryList;

    default:
      return g2ConvertHistoryList;
  }
}

export function org2OrgHistoryListReducer(org2OrgHistoryList = [], action) {
  switch (action.type) {
    case ActionTypes.ADD_ORG_TO_ORG_HISTORY_LIST:
      return action.org2OrgHistoryList;

    default:
      return org2OrgHistoryList;
  }
}

export function queueActionDataReducer(queueActionData = {}, action) {
  switch (action.type) {
    case ActionTypes.ADD_QUEUE_ACTION_DATA:
      return action.queueActionData;
    case ActionTypes.ADD_QUEUE_ACTION_LOG:
      return action.queueActionLog;
    default:
      return queueActionData;
  }
}

export function lastAccessedKioskReducer(hostInfoId = {}, action) {
  switch (action.type) {
    case ActionTypes.UPDATE_LAST_ACCESSED_KIOSK:
      return action.hostInfoId;

    default:
      return hostInfoId;
  }
}



// WEBPACK FOOTER //
// ./src/redux/reducers.js